import { useState } from "react";
import { useToast } from "@/components/base/Toast";
import type { Integration } from "@/mocks/integrationsData";

type Tab = "api" | "webhook" | "mapping" | "test";

interface Props {
  integration: Integration;
  onClose: () => void;
}

export function ConfigureModal({ integration, onClose }: Props) {
  const { showToast } = useToast();
  const [activeTab, setActiveTab] = useState<Tab>("api");
  const [syncing, setSyncing] = useState(false);

  const tabs: { key: Tab; label: string }[] = [
    { key: "api", label: "API Settings" },
    { key: "webhook", label: "Webhook" },
    { key: "mapping", label: "Field Mapping" },
    { key: "test", label: "Test" },
  ];

  const handleCopy = (value: string, label: string) => {
    navigator.clipboard.writeText(value.replace(/\*/g, "x"));
    showToast(`${label} copied to clipboard`, "success");
  };

  const handleSave = () => {
    setSyncing(true);
    setTimeout(() => {
      setSyncing(false);
      showToast("Integration settings saved successfully", "success");
      onClose();
    }, 600);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative bg-white rounded-xl shadow-lg w-full max-w-lg max-h-[85vh] flex flex-col">
        {/* Modal header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <div>
            <h3 className="text-lg font-semibold text-[#111827]">
              Configure {integration.name} Integration
            </h3>
            <p className="text-xs text-[#6b7280] mt-0.5">
              {integration.type}
            </p>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-100 text-gray-400 hover:text-gray-600 transition-colors"
          >
            <i className="ri-close-line text-lg" />
          </button>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-gray-200 px-6">
          {tabs.map((tab) => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={`px-4 py-2.5 text-sm font-medium transition-colors border-b-2 -mb-px whitespace-nowrap ${
                activeTab === tab.key
                  ? "border-indigo-600 text-indigo-600"
                  : "border-transparent text-[#6b7280] hover:text-[#374151]"
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Tab content */}
        <div className="flex-1 overflow-y-auto px-6 py-5">
          {activeTab === "api" && (
            <div className="space-y-5">
              <div>
                <label className="block text-xs font-medium text-[#6b7280] mb-1.5">
                  API Key
                </label>
                <div className="flex items-center gap-2">
                  <input
                    type="text"
                    value={integration.apiKey || ""}
                    readOnly
                    className="flex-1 bg-gray-50 border border-gray-200 rounded-lg px-3 py-2 text-sm text-[#374151] font-mono"
                  />
                  <button
                    onClick={() => handleCopy(integration.apiKey || "", "API Key")}
                    className="w-8 h-8 flex items-center justify-center rounded-lg bg-indigo-50 text-indigo-600 hover:bg-indigo-100 transition-colors flex-shrink-0"
                  >
                    <i className="ri-file-copy-line" />
                  </button>
                </div>
                <button
                  onClick={() => showToast("New API key generated", "success")}
                  className="text-xs text-indigo-600 hover:text-indigo-700 mt-1.5 font-medium whitespace-nowrap"
                >
                  Regenerate API Key
                </button>
              </div>

              <div>
                <label className="block text-xs font-medium text-[#6b7280] mb-1.5">
                  API Secret
                </label>
                <div className="flex items-center gap-2">
                  <input
                    type="text"
                    value={integration.apiSecret || ""}
                    readOnly
                    className="flex-1 bg-gray-50 border border-gray-200 rounded-lg px-3 py-2 text-sm text-[#374151] font-mono"
                  />
                  <button
                    onClick={() => handleCopy(integration.apiSecret || "", "API Secret")}
                    className="w-8 h-8 flex items-center justify-center rounded-lg bg-indigo-50 text-indigo-600 hover:bg-indigo-100 transition-colors flex-shrink-0"
                  >
                    <i className="ri-file-copy-line" />
                  </button>
                </div>
              </div>

              <div>
                <label className="block text-xs font-medium text-[#6b7280] mb-1.5">
                  Account ID
                </label>
                <input
                  type="text"
                  value={integration.accountId || ""}
                  readOnly
                  className="w-full bg-gray-50 border border-gray-200 rounded-lg px-3 py-2 text-sm text-[#374151]"
                />
              </div>

              <div>
                <label className="block text-xs font-medium text-[#6b7280] mb-1.5">
                  Sync Frequency
                </label>
                <select
                  defaultValue={integration.syncFrequency || "Real-time"}
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm text-[#374151] bg-white focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                >
                  <option>Real-time</option>
                  <option>Every 5 mins</option>
                  <option>Every 15 mins</option>
                  <option>Hourly</option>
                </select>
              </div>

              <div className="flex items-center justify-between bg-gray-50 rounded-lg px-4 py-3">
                <div>
                  <p className="text-sm font-medium text-[#374151]">Auto-assign using assignment rules</p>
                  <p className="text-xs text-[#6b7280] mt-0.5">New leads are automatically routed per active rules</p>
                </div>
                <button
                  className={`relative w-10 h-6 rounded-full transition-colors ${
                    integration.autoAssign ? "bg-indigo-600" : "bg-gray-300"
                  }`}
                  onClick={() => showToast(integration.autoAssign ? "Auto-assign disabled" : "Auto-assign enabled", "info")}
                >
                  <span
                    className={`absolute top-0.5 w-5 h-5 rounded-full bg-white shadow transition-transform ${
                      integration.autoAssign ? "left-[18px]" : "left-0.5"
                    }`}
                  />
                </button>
              </div>
            </div>
          )}

          {activeTab === "webhook" && (
            <div className="space-y-5">
              <div>
                <label className="block text-xs font-medium text-[#6b7280] mb-1.5">
                  Webhook URL
                </label>
                <div className="flex items-center gap-2">
                  <input
                    type="text"
                    value={integration.webhookUrl || ""}
                    readOnly
                    className="flex-1 bg-gray-50 border border-gray-200 rounded-lg px-3 py-2 text-xs text-[#374151] font-mono"
                  />
                  <button
                    onClick={() => handleCopy(integration.webhookUrl || "", "Webhook URL")}
                    className="px-3 py-2 bg-indigo-600 text-white text-xs font-medium rounded-lg hover:bg-indigo-700 transition-colors whitespace-nowrap"
                  >
                    Copy
                  </button>
                </div>
              </div>

              <div>
                <label className="block text-xs font-medium text-[#6b7280] mb-1.5">
                  Secret Key
                </label>
                <div className="flex items-center gap-2">
                  <input
                    type="text"
                    value={integration.webhookSecret || ""}
                    readOnly
                    className="flex-1 bg-gray-50 border border-gray-200 rounded-lg px-3 py-2 text-sm text-[#374151] font-mono"
                  />
                  <button
                    onClick={() => handleCopy(integration.webhookSecret || "", "Secret Key")}
                    className="w-8 h-8 flex items-center justify-center rounded-lg bg-indigo-50 text-indigo-600 hover:bg-indigo-100 transition-colors flex-shrink-0"
                  >
                    <i className="ri-file-copy-line" />
                  </button>
                </div>
                <button
                  onClick={() => showToast("New webhook secret generated", "success")}
                  className="text-xs text-indigo-600 hover:text-indigo-700 mt-1.5 font-medium whitespace-nowrap"
                >
                  Regenerate Secret
                </button>
              </div>

              <div>
                <label className="block text-xs font-medium text-[#6b7280] mb-2">
                  Subscribed Events
                </label>
                <div className="space-y-2.5">
                  {[
                    { label: "New Lead Received", checked: true },
                    { label: "Lead Updated", checked: true },
                    { label: "Lead Deleted", checked: false },
                  ].map((event) => (
                    <label
                      key={event.label}
                      className="flex items-center gap-3 cursor-pointer"
                    >
                      <div
                        className={`w-4 h-4 rounded border-2 flex items-center justify-center transition-colors ${
                          event.checked
                            ? "bg-indigo-600 border-indigo-600"
                            : "border-gray-300"
                        }`}
                      >
                        {event.checked && (
                          <i className="ri-check-line text-white text-[10px]" />
                        )}
                      </div>
                      <span className="text-sm text-[#374151]">{event.label}</span>
                    </label>
                  ))}
                </div>
              </div>

              <button
                onClick={() => showToast("Test webhook sent successfully", "success")}
                className="w-full px-4 py-2.5 border border-indigo-600 text-indigo-600 text-sm font-medium rounded-lg hover:bg-indigo-50 transition-colors whitespace-nowrap"
              >
                Test Webhook
              </button>
            </div>
          )}

          {activeTab === "mapping" && (
            <div>
              <p className="text-xs text-[#6b7280] mb-4">
                Map {integration.name} fields to CRM lead fields
              </p>
              <div className="border border-gray-200 rounded-lg overflow-hidden">
                <div className="grid grid-cols-2 bg-gray-50 px-4 py-2.5 border-b border-gray-200">
                  <span className="text-xs font-semibold text-[#6b7280] uppercase tracking-wider">
                    {integration.name} Field
                  </span>
                  <span className="text-xs font-semibold text-[#6b7280] uppercase tracking-wider">
                    CRM Field
                  </span>
                </div>
                {integration.fieldMapping?.map((row, i) => (
                  <div
                    key={row.sourceField}
                    className={`grid grid-cols-2 px-4 py-2.5 ${
                      i % 2 === 0 ? "bg-white" : "bg-gray-50/50"
                    }`}
                  >
                    <span className="text-sm text-[#374151] font-mono text-xs">
                      {row.sourceField}
                    </span>
                    <select
                      defaultValue={row.crmField}
                      className="border border-gray-200 rounded px-2 py-1 text-xs text-[#374151] bg-white focus:outline-none focus:ring-1 focus:ring-indigo-500"
                    >
                      <option>Lead Name</option>
                      <option>Phone</option>
                      <option>Email</option>
                      <option>Notes</option>
                      <option>City</option>
                      <option>Source Notes</option>
                      <option>Company</option>
                      <option>Ignore</option>
                    </select>
                  </div>
                ))}
              </div>
            </div>
          )}

          {activeTab === "test" && (
            <div className="space-y-5">
              <div>
                <h4 className="text-sm font-semibold text-[#111827] mb-2">
                  Send a Test Lead
                </h4>
                <p className="text-xs text-[#6b7280] mb-3">
                  Send a mock lead to verify the integration works end-to-end
                </p>
                <button
                  onClick={() =>
                    showToast("Mock lead sent — \"Test User\" created successfully", "success")
                  }
                  className="px-4 py-2.5 bg-indigo-600 text-white text-sm font-medium rounded-lg hover:bg-indigo-700 transition-colors whitespace-nowrap"
                >
                  Send Mock Lead
                </button>
              </div>

              <div className="border-t border-gray-200 pt-4">
                <h4 className="text-sm font-semibold text-[#111827] mb-2">
                  Test Results
                </h4>
                <div className="bg-green-50 border border-green-200 rounded-lg px-4 py-3">
                  <div className="flex items-center gap-2">
                    <div className="w-5 h-5 rounded-full bg-green-500 flex items-center justify-center flex-shrink-0">
                      <i className="ri-check-line text-white text-xs" />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-green-800">
                        Last test: Success
                      </p>
                      <p className="text-xs text-green-700">
                        Lead &apos;Test User&apos; created at 10:30 AM today
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Modal footer */}
        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-200">
          <button
            onClick={onClose}
            className="px-4 py-2 border border-gray-300 text-sm font-medium text-[#374151] rounded-lg hover:bg-gray-50 transition-colors whitespace-nowrap"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={syncing}
            className="px-5 py-2 bg-indigo-600 text-white text-sm font-medium rounded-lg hover:bg-indigo-700 transition-colors disabled:opacity-60 whitespace-nowrap"
          >
            {syncing ? "Saving..." : "Save Changes"}
          </button>
        </div>
      </div>
    </div>
  );
}